import type { LanguageType } from '@/types/language';

/** A name written once per language, so a mark cannot be named twice. */
export type PartnerText = Record<LanguageType, string>;

/**
 * ### Partner
 *
 * One organisation the work has been done with. The mark and the group it
 * belongs to are language-independent and stated once; only the name is
 * written per language. Holding it the other way round — a full list per
 * locale — is what let the Korean and English grids drift apart before.
 */
export type Partner = {
  /** Stable identifier, used as the React key and for tests */
  id: string;

  /** Display name per language, also the alt text where a mark is shown */
  name: PartnerText;

  /**
   * The brand mark. Absent while the asset is awaited, in which case the name
   * is set as type in the mark's place.
   */
  logo?: PartnerLogo;
};

/**
 * ### PartnerLogo
 *
 * A mark file and its shape.
 *
 * The aspect ratio travels with the file because the wall sizes every mark
 * by it: fitting each one into the same box instead makes a wide wordmark
 * a thin strip and a square mark a block, so the marks stop reading as
 * equals. `partners.test.ts` checks the ratio against the file itself.
 */
export type PartnerLogo = {
  /** File under `/images/apps/home/`, cropped to the mark with no padding */
  file: string;
  /** Width over height of the file, to two decimals */
  aspectRatio: number;
};

export type PartnerGroupId = 'industrial' | 'safety' | 'collaboration';

/**
 * ### PartnerGroup
 *
 * One band of the wall. Current customers and past collaborators are held
 * apart because they are not the same kind of relationship, and the band
 * heading is what says which is which.
 */
export type PartnerGroup = {
  id: PartnerGroupId;
  title: PartnerText;
  members: ReadonlyArray<Partner>;
};

/**
 * ### PARTNER_GROUPS
 *
 * The one list of organisations the site names, for the About page's bands
 * and the home page's wall alike.
 *
 * Customer names are published only within what has been cleared. Adding a
 * name here publishes it on two pages at once.
 *
 * @see {@link ABOUT_MILESTONES_BY_LOCALE} The dated record of the same work
 */
export const PARTNER_GROUPS: ReadonlyArray<PartnerGroup> = [
  {
    id: 'industrial',
    title: { ko: 'Industrial 고객사', en: 'Industrial customers' },
    members: [
      {
        id: 'hyosung-electric',
        name: { ko: '효성전기', en: 'Hyosung Electric' },
        logo: {
          file: 'logo_hyosung_electric-transparent.png',
          aspectRatio: 4.07,
        },
      },
      {
        id: 'korail',
        name: { ko: '코레일', en: 'KORAIL' },
        logo: { file: 'logo_korail-transparent.png', aspectRatio: 4.1 },
      },
      {
        id: 'rok-army',
        name: { ko: '대한민국 육군', en: 'Republic of Korea Army' },
        logo: { file: 'logo_korea_army-hd.webp', aspectRatio: 3.01 },
      },
      {
        id: 'thailand-automotive-institute',
        name: {
          ko: 'Thailand Automotive Institute',
          en: 'Thailand Automotive Institute',
        },
        logo: { file: 'logo_tai-transparent.png', aspectRatio: 3.06 },
      },
    ],
  },
  {
    id: 'safety',
    title: { ko: 'Safety 고객사', en: 'Safety customers' },
    members: [
      {
        id: 'sejong-complex-gym',
        name: {
          ko: '정부세종청사 체육관',
          en: 'Sejong Government Complex Gymnasium',
        },
        logo: { file: 'logo_sejong-transparent.png', aspectRatio: 3.84 },
      },
      {
        id: 'incheon-transit',
        name: { ko: '인천교통공사', en: 'Incheon Transit Corporation' },
        logo: {
          file: 'logo_incheon_transit-transparent.png',
          aspectRatio: 4.08,
        },
      },
      {
        id: 'naejangsan',
        name: {
          ko: '내장산국립공원사무소',
          en: 'Naejangsan National Park Office',
        },
        logo: { file: 'logo_naejangsan-transparent.png', aspectRatio: 3.59 },
      },
      {
        id: 'ifez',
        name: { ko: '인천경제자유구역 IFEZ', en: 'IFEZ' },
        logo: { file: 'logo_ifez.svg', aspectRatio: 3.26 },
      },
      {
        id: 'high1',
        name: { ko: '하이원리조트', en: 'High1 Resort' },
        logo: { file: 'logo_highone-hd.webp', aspectRatio: 2.59 },
      },
      {
        id: 'lotte-enc',
        name: { ko: '롯데건설', en: 'Lotte E&C' },
        logo: { file: 'logo_lotte.svg', aspectRatio: 3.0 },
      },
      {
        id: 'yangcheon',
        name: {
          ko: '양천구시설관리공단',
          en: 'Yangcheon-gu Facilities Management Corporation',
        },
        logo: { file: 'logo_yangcheon-transparent.png', aspectRatio: 3.7 },
      },
      {
        id: 'dobong',
        name: { ko: '도봉구', en: 'Dobong-gu' },
        logo: { file: 'logo_dobong-transparent.png', aspectRatio: 3.96 },
      },
      {
        id: 'daegu-seogu',
        name: { ko: '대구광역시 서구', en: 'Seo-gu, Daegu' },
        logo: { file: 'logo_daegu_seogu-transparent.png', aspectRatio: 4.32 },
      },
      {
        id: 'seongju',
        name: { ko: '성주군', en: 'Seongju-gun' },
        logo: { file: 'logo_seongju-transparent.png', aspectRatio: 2.57 },
      },
    ],
  },
  {
    id: 'collaboration',
    title: { ko: '주요 협력 이력', en: 'Past clients and partners' },
    members: [
      { id: 'meta', name: { ko: 'Meta', en: 'Meta' } },
      { id: 'apple', name: { ko: 'Apple', en: 'Apple' } },
      {
        id: 'google',
        name: { ko: 'Google', en: 'Google' },
        logo: { file: 'logo_google.svg', aspectRatio: 2.93 },
      },
      {
        id: 'sony',
        name: { ko: 'Sony', en: 'Sony' },
        logo: { file: 'logo_sony.svg', aspectRatio: 5.86 },
      },
      {
        id: 'naver',
        name: { ko: 'Naver', en: 'Naver' },
        logo: { file: 'logo_naver.svg', aspectRatio: 5.09 },
      },
      {
        id: 'hanwha',
        name: { ko: 'Hanwha', en: 'Hanwha' },
        logo: { file: 'logo_hanwha.svg', aspectRatio: 3.7 },
      },
      {
        id: 'nii',
        name: { ko: 'NII', en: 'NII' },
        logo: { file: 'logo_nii-hd.webp', aspectRatio: 5.26 },
      },
      { id: 'cappella', name: { ko: 'Cappella', en: 'Cappella' } },
      {
        id: 'korea-university-medicine',
        name: { ko: '고려대학교의료원', en: 'Korea University Medicine' },
        logo: { file: 'logo_korea_uv_medi.svg', aspectRatio: 4.47 },
      },
      {
        id: 'seoul-national-university',
        name: { ko: '서울대학교', en: 'Seoul National University' },
        logo: { file: 'logo_seoul_uv-hd.webp', aspectRatio: 2.33 },
      },
      {
        id: 'yonsei-university',
        name: { ko: '연세대학교', en: 'Yonsei University' },
        logo: { file: 'logo_yonsei_uv.svg', aspectRatio: 3.38 },
      },
      {
        id: 'gwangju',
        name: { ko: '광주광역시', en: 'Gwangju Metropolitan City' },
        logo: { file: 'logo_gwangju.svg', aspectRatio: 3.57 },
      },
      {
        id: 'kangwon-land',
        name: { ko: '강원랜드', en: 'Kangwon Land' },
        logo: { file: 'logo_kangwonland.svg', aspectRatio: 2.77 },
      },
      {
        id: 'koica',
        name: { ko: 'KOICA', en: 'KOICA' },
        logo: { file: 'logo_koica.svg', aspectRatio: 3.88 },
      },
      {
        id: 'lg-cns',
        name: { ko: 'LG CNS', en: 'LG CNS' },
        logo: { file: 'logo_lg_cns.svg', aspectRatio: 4.1 },
      },
      {
        id: 'kb',
        name: { ko: 'KB', en: 'KB' },
        logo: { file: 'logo_kb.svg', aspectRatio: 3.04 },
      },
      {
        id: 'kt',
        name: { ko: 'KT', en: 'KT' },
        logo: { file: 'logo_kt.svg', aspectRatio: 1.23 },
      },
    ],
  },
];

/** One organisation with its name already resolved to a language. */
export type LocalizedPartner = {
  id: string;
  name: string;
  logo?: PartnerLogo;
};

/** One band with its heading and members resolved to a language. */
export type LocalizedPartnerGroup = {
  id: PartnerGroupId;
  title: string;
  members: ReadonlyArray<LocalizedPartner>;
};

/**
 * ### selectPartnerGroups
 *
 * The bands as the About page renders them, in the order they are declared.
 *
 * @param locale Which language to resolve the names in
 * @returns Every group, members included
 */
export function selectPartnerGroups(
  locale: LanguageType,
): ReadonlyArray<LocalizedPartnerGroup> {
  return PARTNER_GROUPS.map((group) => ({
    id: group.id,
    title: group.title[locale],
    members: group.members.map((member) => ({
      id: member.id,
      name: member.name[locale],
      logo: member.logo,
    })),
  }));
}

/** One organisation that has a mark to show, so `logo` is never absent. */
export type PartnerMark = {
  id: string;
  name: string;
  logo: PartnerLogo;
};

/**
 * ### selectPartnerMarks
 *
 * Every organisation whose mark is in the repository, flattened across the
 * groups, for the home page's wall. The home wall carries no headings and
 * so says nothing about which relationship is which; it only shows the
 * marks, and a name without one has nothing to contribute to it.
 *
 * @param locale Which language to resolve the names in
 * @returns The marks, customers before collaborators
 */
export function selectPartnerMarks(
  locale: LanguageType,
): ReadonlyArray<PartnerMark> {
  return PARTNER_GROUPS.flatMap((group) =>
    group.members.flatMap((member) =>
      member.logo
        ? [{ id: member.id, name: member.name[locale], logo: member.logo }]
        : [],
    ),
  );
}
